import { SimMapConfigModule } from '@yaak/components/services/api/api'

export const formatGearModes = (modes: string[]): string =>
  modes?.length
    ? modes.map((m) => m.charAt(0).toUpperCase() + m.slice(1)).join(', ')
    : '-'

export const groupModules = <T extends { groupName: string }>(
  modules: T[]
): { groupName: string, modules: T[] }[] => {
  const groups: Record<string, T[]> = {}
  const order: string[] = []

  for (const mod of modules) {
    const key = mod.groupName || 'Other'
    if (!groups[key]) {
      groups[key] = []
      order.push(key)
    }
    groups[key].push(mod)
  }

  return order.map((name) => ({ groupName: name, modules: groups[name] }))
}

export const getEnabledModuleIds = (modules: SimMapConfigModule[]): string[] =>
  modules.filter((m) => m.enabled).map((m) => m.moduleId)

export const hasModuleConfigChanges = (
  localModules: SimMapConfigModule[],
  originalModules: SimMapConfigModule[]
): boolean =>
  localModules.some((m) => {
    const orig = originalModules.find((om) => om.moduleId === m.moduleId)
    return orig?.enabled !== m.enabled
  })

export const hasActiveChanges = (
  localModules: { moduleId: string, active: boolean }[],
  originalModules: { moduleId: string, active: boolean }[]
): boolean =>
  localModules.some((m) => {
    const orig = originalModules.find((om) => om.moduleId === m.moduleId)
    return orig?.active !== m.active
  })

export const getActivationDiff = (
  localModules: { moduleId: string, active: boolean }[],
  originalModules: { moduleId: string, active: boolean }[]
): { toActivate: string[], toDeactivate: string[] } => {
  const toActivate: string[] = []
  const toDeactivate: string[] = []

  for (const local of localModules) {
    const orig = originalModules.find((om) => om.moduleId === local.moduleId)
    if (!orig || orig.active === local.active) continue
    if (local.active) {
      toActivate.push(local.moduleId)
    } else {
      toDeactivate.push(local.moduleId)
    }
  }

  return { toActivate, toDeactivate }
}

export const hasCustomChanges = (
  localModules: { moduleId: string, isCustom: boolean }[],
  originalModules: { moduleId: string, isCustom: boolean }[]
): boolean =>
  localModules.some((m) => {
    const orig = originalModules.find((om) => om.moduleId === m.moduleId)
    return orig?.isCustom !== m.isCustom
  })

export const getCustomDiff = (
  localModules: { moduleId: string, isCustom: boolean }[],
  originalModules: { moduleId: string, isCustom: boolean }[]
): { toMarkCustom: string[], toUnmarkCustom: string[] } => {
  const toMarkCustom: string[] = []
  const toUnmarkCustom: string[] = []

  for (const local of localModules) {
    const orig = originalModules.find((om) => om.moduleId === local.moduleId)
    if (!orig || orig.isCustom === local.isCustom) continue
    if (local.isCustom) {
      toMarkCustom.push(local.moduleId)
    } else {
      toUnmarkCustom.push(local.moduleId)
    }
  }

  return { toMarkCustom, toUnmarkCustom }
}

export const allowlistPartnerIds = (
  visiblePartnerIds: string[],
  originalPartnerIds: string[],
  whitelistedPartnerIds: string[]
): string[] => {
  const visible = new Set(visiblePartnerIds)
  const hidden = originalPartnerIds.filter((id) => !visible.has(id))
  return [...hidden, ...whitelistedPartnerIds]
}

export const hasAllowlistChanges = (
  partnerIds: string[],
  originalPartnerIds: string[]
): boolean => {
  if (partnerIds.length !== originalPartnerIds.length) return true
  const original = new Set(originalPartnerIds)
  return partnerIds.some((id) => !original.has(id))
}
